import React, { useEffect, useMemo, useState } from "react";

import Button from "@mui/material/Button";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import PageContainer from "components/atoms/PageContainer";
import GenericTabs from "components/GenericTabs/GenericTabs";

import {
    getExcistTrackersRedux,
    getSingleTrackerRedux,
} from "../../../redux/slices/trackers/trackers.asyncActions";
import { clearState } from "../../../redux/slices/trackers/trackers.slice";
import DeleteTrackerDialog from "../DeleteTrackerDialog";
import CookieConsent from "./CookieConsent";
import EventsConfig from "./EventsConfig";
import GeneralSettings from "./GeneralSettings";
import TestEvents from "./TestEvents";
import "./trackerSetting.css";
import InlineTrackerNameEdit from "components/TrackerNameEdit/InlineTrackerNameEdit";

const WEB2APP_SOURCE_CODES = ["appsflyer", "adjust"];

const TrackerSetting = () => {
    const [deleteModalShow, setDeleteModalShow] = useState(false);
    const dispatch = useDispatch();
    const params = useParams();
    const tracker = useSelector(
        (state) => state.allTrackersState.singleTracker,
    );
    const singleTrackerStatus = useSelector(
        (state) => state.allTrackersState.singleTrackerStatus,
    );
    const language = useSelector((state) => state.languagesState.languages);
    const history = useNavigate();

    const sources = tracker?.tracker?.sources || [];

    const hasJsSource = sources.some((src) => src?.source_type?.code === "js");

    const hasWeb2AppSource = sources.some((src) =>
        WEB2APP_SOURCE_CODES.includes(src?.source_type?.code),
    );

    useEffect(() => {
        dispatch(clearState());
        const request = dispatch(getSingleTrackerRedux(params.id));
        // Abort on id change/unmount so a late response can't land in the store.
        return () => request.abort();
    }, [params.id]);

    const handleDeleteSuccess = () => {
        dispatch(getExcistTrackersRedux());
        setTimeout(() => {
            history("/trackers");
        }, 1000);
    };

    const defaultTab = hasWeb2AppSource ? "general" : "test-events";

    const tabs = useMemo(() => {
        const tabList = [];
        if (hasWeb2AppSource) {
            tabList.push({
                value: "general",
                label:
                    language?.trackers_page?.general_settings ??
                    "General Settings",
                content: <GeneralSettings trackerId={params.id} />,
            });
        }
        tabList.push({
            value: "test-events",
            label: language?.trackers_page?.test_events ?? "Test Events",
            content: <TestEvents />,
        });
        tabList.push({
            value: "events-config",
            label:
                language?.trackers_page?.events_config ?? "Event Configuration",
            content: <EventsConfig trackerId={params.id} />,
        });
        if (hasJsSource) {
            tabList.push({
                value: "cookie-consent",
                label:
                    language?.trackers_page?.cookie_consent ?? "Cookie Consent",
                content: <CookieConsent />,
            });
        }
        return tabList;
    }, [hasWeb2AppSource, hasJsSource, language, params.id]);

    return (
        <PageContainer
            title={"Tracker Settings"}
            subtitle={
                <InlineTrackerNameEdit
                    trackerId={params.id}
                    name={tracker?.tracker?.tracker_name}
                    loading={
                        singleTrackerStatus === "idle" ||
                        singleTrackerStatus === "loading"
                    }
                />
            }   
            additionalButtons={
                singleTrackerStatus === "success" && (
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setDeleteModalShow(true)}
                        style={{ minWidth: "unset" }}
                        data-cy="tracker-setting-delete"
                    >
                        {"Delete Tracker"}
                    </Button>
                )
            }
            loading={singleTrackerStatus === "loading"}
            variant="detail"
        >
            {singleTrackerStatus === "success" && (
                <GenericTabs
                    key={params.id}
                    tabs={tabs}
                    defaultTab={defaultTab}
                    elevation={0}
                    contentPadding={0}
                />
            )}

            <DeleteTrackerDialog
                open={deleteModalShow}
                onClose={() => setDeleteModalShow(false)}
                tracker={tracker?.tracker}
                language={language}
                onDeleteSuccess={handleDeleteSuccess}
            />
        </PageContainer>
    );
};

export default TrackerSetting;
